import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getNatureOfGroups, getMasterGroupsByNature, getSubGroupsByGroupId, getApprovalStatusClient, getApprovalStatusSubClient,
    getStatesList, getAllLedgerDetails,
    getAllClientDetails, getClientDetailsByCode, getTaxesForClient, saveNewClient, updateClient,
    checkSubClientEligibility, getSubClientsByClientCode, getSubClientDetails, saveNewSubClient, updateSubClientDetails,
} from '../../api/AccountsAPI/clientAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Thunks ─────────────────────────────────────────────────────────────────────

export const fetchNatureOfGroups = createAsyncThunk('client/fetchNatureOfGroups', async (_, { rejectWithValue }) => {
    try { const res = await getNatureOfGroups(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch nature of groups')); }
});

export const fetchMasterGroups = createAsyncThunk('client/fetchMasterGroups', async (natureGroupId, { rejectWithValue }) => {
    try { const res = await getMasterGroupsByNature(natureGroupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchSubGroups = createAsyncThunk('client/fetchSubGroups', async (groupId, { rejectWithValue }) => {
    try { const res = await getSubGroupsByGroupId(groupId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub groups')); }
});

export const fetchApprovalStatusClient = createAsyncThunk('client/fetchApprovalStatus', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusClient(roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const fetchApprovalStatusSubClient = createAsyncThunk('client/fetchApprovalStatusSubClient', async (roleId, { rejectWithValue }) => {
    try { const res = await getApprovalStatusSubClient(roleId); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch allowed actions')); }
});

export const fetchStates = createAsyncThunk('client/fetchStates', async (_, { rejectWithValue }) => {
    try { const res = await getStatesList(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch states')); }
});

export const fetchClientGrid = createAsyncThunk('client/fetchGrid', async (_, { rejectWithValue }) => {
    try { const res = await getAllClientDetails(); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch clients')); }
});

export const fetchClientByCode = createAsyncThunk('client/fetchByCode', async (clientCode, { rejectWithValue }) => {
    try { const res = await getClientDetailsByCode(clientCode); return res?.Data?.[0] || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch client details')); }
});

export const fetchClientTaxes = createAsyncThunk('client/fetchClientTaxes', async (clientCode, { rejectWithValue }) => {
    try { const res = await getTaxesForClient(clientCode); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch GST details')); }
});

export const submitNewClient = createAsyncThunk('client/submitNew', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewClient(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save client');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save client')); }
});

export const submitUpdateClient = createAsyncThunk('client/submitUpdate', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateClient(payload);
        const status = res?.Data;
        if (status !== 'Updated') return rejectWithValue(status || 'Failed to update client');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update client')); }
});

// ── Sub Client ─────────────────────────────────────────────────────────────────

// Cached per client code so the eligibility check (which actions to offer) only ever
// fires once per row, on demand — not for every row up front like the legacy grid did.
export const fetchSubClientEligibility = createAsyncThunk(
    'client/fetchSubClientEligibility',
    async ({ clientCode, roleId }, { rejectWithValue }) => {
        try {
            const res = await checkSubClientEligibility(clientCode, roleId);
            const info = res?.Data?.[0] || { WFExist: 0, SubClientCExist: 0 };
            return { clientCode, info };
        } catch (e) { return rejectWithValue(err(e, 'Failed to check sub client eligibility')); }
    }
);

export const fetchSubClientsByClientCode = createAsyncThunk('client/fetchSubClientsByClientCode', async (clientCode, { rejectWithValue }) => {
    try { const res = await getSubClientsByClientCode(clientCode); return res?.Data || []; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub clients')); }
});

export const fetchSubClientDetails = createAsyncThunk('client/fetchSubClientDetails', async ({ clientCode, subClient }, { rejectWithValue }) => {
    try { const res = await getSubClientDetails(clientCode, subClient); return res?.Data?.[0] || null; } catch (e) { return rejectWithValue(err(e, 'Failed to fetch sub client details')); }
});

// Sub-client ledgers are real (unlike a plain Client), so an edit must round-trip the
// current Balance/Date/ValueType rather than resend blanks — spInsertUpdateSubClient's
// Update branch unconditionally overwrites the ledger row with whatever it's given.
export const fetchSubClientLedger = createAsyncThunk('client/fetchSubClientLedger', async (subClientCode, { rejectWithValue }) => {
    try {
        const res = await getAllLedgerDetails();
        const rows = res?.Data || [];
        const match = rows.find((r) => r.AccHeadRef === 'SubClient' && (r.AccHead || '').split(',')[0] === subClientCode);
        return match || null;
    } catch (e) { return rejectWithValue(err(e, 'Failed to fetch ledger details')); }
});

export const submitNewSubClient = createAsyncThunk('client/submitNewSubClient', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewSubClient(payload);
        const status = res?.Data;
        if (status !== 'Submitted' && status !== 'Submited') return rejectWithValue(status || 'Failed to save sub client');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save sub client')); }
});

export const submitUpdateSubClient = createAsyncThunk('client/submitUpdateSubClient', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateSubClientDetails(payload);
        const status = res?.Data;
        if (status !== 'Updated') return rejectWithValue(status || 'Failed to update sub client');
        return status;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update sub client')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    natureGroups: [], natureGroupsLoading: false,
    masterGroups: [], masterGroupsLoading: false,
    subGroups: [], subGroupsLoading: false,
    allowedActions: [], allowedActionsLoading: false,
    allowedActionsSubClient: [], allowedActionsSubClientLoading: false,
    states: [], statesLoading: false,

    grid: [], gridLoading: false, gridError: null,
    clientByCode: null, clientByCodeLoading: false,
    clientTaxes: [], clientTaxesLoading: false,

    saveLoading: false, saveResult: null, saveError: null,
    updateLoading: false, updateResult: null, updateError: null,

    eligibilityByClient: {}, eligibilityLoadingFor: null,
    subClients: [], subClientsLoading: false,
    subClientDetails: null, subClientDetailsLoading: false,
    subClientLedger: null, subClientLedgerLoading: false,

    saveSubClientLoading: false, saveSubClientResult: null, saveSubClientError: null,
    updateSubClientLoading: false, updateSubClientResult: null, updateSubClientError: null,
};

const clientSlice = createSlice({
    name: 'client',
    initialState,
    reducers: {
        clearMasterGroups: (s) => { s.masterGroups = []; s.subGroups = []; },
        clearSubGroups: (s) => { s.subGroups = []; },
        clearClientByCode: (s) => { s.clientByCode = null; s.clientTaxes = []; },
        clearSaveResult: (s) => { s.saveResult = null; s.saveError = null; },
        clearUpdateResult: (s) => { s.updateResult = null; s.updateError = null; },
        clearSubClients: (s) => { s.subClients = []; s.subClientDetails = null; s.subClientLedger = null; },
        clearSubClientDetails: (s) => { s.subClientDetails = null; s.subClientLedger = null; },
        clearSaveSubClientResult: (s) => { s.saveSubClientResult = null; s.saveSubClientError = null; },
        clearUpdateSubClientResult: (s) => { s.updateSubClientResult = null; s.updateSubClientError = null; },
        invalidateClientEligibility: (s, a) => { delete s.eligibilityByClient[a.payload]; },
        resetClient: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNatureOfGroups.pending, (s) => { s.natureGroupsLoading = true; })
            .addCase(fetchNatureOfGroups.fulfilled, (s, a) => { s.natureGroupsLoading = false; s.natureGroups = a.payload; })
            .addCase(fetchNatureOfGroups.rejected, (s) => { s.natureGroupsLoading = false; });

        builder
            .addCase(fetchMasterGroups.pending, (s) => { s.masterGroupsLoading = true; })
            .addCase(fetchMasterGroups.fulfilled, (s, a) => { s.masterGroupsLoading = false; s.masterGroups = a.payload; })
            .addCase(fetchMasterGroups.rejected, (s) => { s.masterGroupsLoading = false; });

        builder
            .addCase(fetchSubGroups.pending, (s) => { s.subGroupsLoading = true; })
            .addCase(fetchSubGroups.fulfilled, (s, a) => { s.subGroupsLoading = false; s.subGroups = a.payload; })
            .addCase(fetchSubGroups.rejected, (s) => { s.subGroupsLoading = false; });

        builder
            .addCase(fetchApprovalStatusClient.pending, (s) => { s.allowedActionsLoading = true; })
            .addCase(fetchApprovalStatusClient.fulfilled, (s, a) => { s.allowedActionsLoading = false; s.allowedActions = a.payload; })
            .addCase(fetchApprovalStatusClient.rejected, (s) => { s.allowedActionsLoading = false; });

        builder
            .addCase(fetchApprovalStatusSubClient.pending, (s) => { s.allowedActionsSubClientLoading = true; })
            .addCase(fetchApprovalStatusSubClient.fulfilled, (s, a) => { s.allowedActionsSubClientLoading = false; s.allowedActionsSubClient = a.payload; })
            .addCase(fetchApprovalStatusSubClient.rejected, (s) => { s.allowedActionsSubClientLoading = false; });

        builder
            .addCase(fetchStates.pending, (s) => { s.statesLoading = true; })
            .addCase(fetchStates.fulfilled, (s, a) => { s.statesLoading = false; s.states = a.payload; })
            .addCase(fetchStates.rejected, (s) => { s.statesLoading = false; });

        builder
            .addCase(fetchClientGrid.pending, (s) => { s.gridLoading = true; s.gridError = null; })
            .addCase(fetchClientGrid.fulfilled, (s, a) => { s.gridLoading = false; s.grid = a.payload; })
            .addCase(fetchClientGrid.rejected, (s, a) => { s.gridLoading = false; s.gridError = a.payload; });

        builder
            .addCase(fetchClientByCode.pending, (s) => { s.clientByCodeLoading = true; })
            .addCase(fetchClientByCode.fulfilled, (s, a) => { s.clientByCodeLoading = false; s.clientByCode = a.payload; })
            .addCase(fetchClientByCode.rejected, (s) => { s.clientByCodeLoading = false; s.clientByCode = null; });

        builder
            .addCase(fetchClientTaxes.pending, (s) => { s.clientTaxesLoading = true; })
            .addCase(fetchClientTaxes.fulfilled, (s, a) => { s.clientTaxesLoading = false; s.clientTaxes = a.payload; })
            .addCase(fetchClientTaxes.rejected, (s) => { s.clientTaxesLoading = false; s.clientTaxes = []; });

        builder
            .addCase(submitNewClient.pending, (s) => { s.saveLoading = true; s.saveError = null; s.saveResult = null; })
            .addCase(submitNewClient.fulfilled, (s, a) => { s.saveLoading = false; s.saveResult = a.payload; })
            .addCase(submitNewClient.rejected, (s, a) => { s.saveLoading = false; s.saveError = a.payload; });

        builder
            .addCase(submitUpdateClient.pending, (s) => { s.updateLoading = true; s.updateError = null; s.updateResult = null; })
            .addCase(submitUpdateClient.fulfilled, (s, a) => { s.updateLoading = false; s.updateResult = a.payload; })
            .addCase(submitUpdateClient.rejected, (s, a) => { s.updateLoading = false; s.updateError = a.payload; });

        builder
            .addCase(fetchSubClientEligibility.pending, (s, a) => { s.eligibilityLoadingFor = a.meta.arg.clientCode; })
            .addCase(fetchSubClientEligibility.fulfilled, (s, a) => {
                s.eligibilityLoadingFor = null;
                s.eligibilityByClient[a.payload.clientCode] = a.payload.info;
            })
            .addCase(fetchSubClientEligibility.rejected, (s) => { s.eligibilityLoadingFor = null; });

        builder
            .addCase(fetchSubClientsByClientCode.pending, (s) => { s.subClientsLoading = true; })
            .addCase(fetchSubClientsByClientCode.fulfilled, (s, a) => { s.subClientsLoading = false; s.subClients = a.payload; })
            .addCase(fetchSubClientsByClientCode.rejected, (s) => { s.subClientsLoading = false; });

        builder
            .addCase(fetchSubClientDetails.pending, (s) => { s.subClientDetailsLoading = true; })
            .addCase(fetchSubClientDetails.fulfilled, (s, a) => { s.subClientDetailsLoading = false; s.subClientDetails = a.payload; })
            .addCase(fetchSubClientDetails.rejected, (s) => { s.subClientDetailsLoading = false; s.subClientDetails = null; });

        builder
            .addCase(fetchSubClientLedger.pending, (s) => { s.subClientLedgerLoading = true; })
            .addCase(fetchSubClientLedger.fulfilled, (s, a) => { s.subClientLedgerLoading = false; s.subClientLedger = a.payload; })
            .addCase(fetchSubClientLedger.rejected, (s) => { s.subClientLedgerLoading = false; s.subClientLedger = null; });

        builder
            .addCase(submitNewSubClient.pending, (s) => { s.saveSubClientLoading = true; s.saveSubClientError = null; s.saveSubClientResult = null; })
            .addCase(submitNewSubClient.fulfilled, (s, a) => { s.saveSubClientLoading = false; s.saveSubClientResult = a.payload; })
            .addCase(submitNewSubClient.rejected, (s, a) => { s.saveSubClientLoading = false; s.saveSubClientError = a.payload; });

        builder
            .addCase(submitUpdateSubClient.pending, (s) => { s.updateSubClientLoading = true; s.updateSubClientError = null; s.updateSubClientResult = null; })
            .addCase(submitUpdateSubClient.fulfilled, (s, a) => { s.updateSubClientLoading = false; s.updateSubClientResult = a.payload; })
            .addCase(submitUpdateSubClient.rejected, (s, a) => { s.updateSubClientLoading = false; s.updateSubClientError = a.payload; });
    },
});

export const {
    clearMasterGroups, clearSubGroups, clearClientByCode, clearSaveResult, clearUpdateResult,
    clearSubClients, clearSubClientDetails, clearSaveSubClientResult, clearUpdateSubClientResult,
    invalidateClientEligibility, resetClient,
} = clientSlice.actions;

export const selectClient = (s) => s.client;

export default clientSlice.reducer;
